<script setup lang="ts">
import { ref, watch } from 'vue'
import { validatePasswordStrength, changePassword } from '../services/authService'
import { useUiStore } from '../stores/uiStore'
import { useUserStore } from '../stores/userStore'

interface Props {
  isOpen: boolean
  userId: number
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const uiStore = useUiStore()
const userStore = useUserStore()

const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const oldPasswordError = ref('')
const newPasswordError = ref('')
const confirmPasswordError = ref('')
const successMsg = ref('')
const isLoading = ref(false)

const resetForm = () => {
  oldPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
  oldPasswordError.value = ''
  newPasswordError.value = ''
  confirmPasswordError.value = ''
  successMsg.value = ''
}

const handleConfirm = async () => {
  oldPasswordError.value = ''
  newPasswordError.value = ''
  confirmPasswordError.value = ''
  successMsg.value = ''

  if (!oldPassword.value) {
    oldPasswordError.value = '此欄不可留白'
    return
  }
  if (!newPassword.value) {
    newPasswordError.value = '此欄不可留白'
    return
  }
  if (!confirmPassword.value) {
    confirmPasswordError.value = '此欄不可留白'
    return
  }

  const strength = validatePasswordStrength(newPassword.value)
  if (!strength.valid) {
    newPasswordError.value = strength.error || '密碼強度不足'
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    confirmPasswordError.value = '與新密碼不符'
    return
  }

  if (oldPassword.value === newPassword.value) {
    newPasswordError.value = '新密碼不可與舊密碼相同'
    return
  }

  isLoading.value = true

  if (!userStore.csrfToken) {
    oldPasswordError.value = '會話已過期，請重新登入'
    isLoading.value = false
    return
  }

  const result = await changePassword(
    userStore.currentUser?.id || 0,
    oldPassword.value,
    newPassword.value
  )

  if (result.success) {
    successMsg.value = '已儲存新密碼，之後請改用新密碼登入'
    setTimeout(() => {
      emit('close')
    }, 3000)
  } else {
    if (result.error?.includes('密碼錯誤')) {
      oldPasswordError.value = '密碼錯誤'
    } else if (result.error?.includes('新密碼不可與舊密碼相同')) {
      newPasswordError.value = '新密碼不可與舊密碼相同'
    } else if (result.error?.includes('密碼強度不足')) {
      newPasswordError.value = '密碼強度不足'
    } else {
      oldPasswordError.value = result.error || '密碼變更失敗'
    }
  }

  isLoading.value = false
}

const handleCancel = () => {
  resetForm()
  emit('close')
}

watch(() => props.isOpen, (newVal) => {
  if (newVal === true) {
    resetForm()
  }
})
</script>

<template>
  <div v-if="isOpen" class="password-dialog-overlay">
    <div class="password-dialog" @click.stop>
      <div class="dialog-header">
        <h2>密碼變更</h2>
        <button class="close-btn" @click="handleCancel">×</button>
      </div>

      <div class="dialog-content">
        <div class="form-group">
          <label>舊密碼</label>
          <input
            v-model="oldPassword"
            type="password"
            placeholder="輸入舊密碼"
            class="form-input"
            :disabled="isLoading || !!successMsg"
          />
          <div v-if="oldPasswordError" class="error-text">{{ oldPasswordError }}</div>
        </div>

        <div class="form-group">
          <label>新密碼</label>
          <input
            v-model="newPassword"
            type="password"
            placeholder="輸入新密碼"
            class="form-input"
            :disabled="isLoading || !!successMsg"
          />
          <div v-if="newPasswordError" class="error-text">{{ newPasswordError }}</div>
        </div>

        <div class="form-group">
          <label>確認密碼</label>
          <input
            v-model="confirmPassword"
            type="password"
            placeholder="再次輸入新密碼"
            class="form-input"
            :disabled="isLoading || !!successMsg"
          />
          <div v-if="confirmPasswordError" class="error-text">{{ confirmPasswordError }}</div>
        </div>

        <div v-if="successMsg" class="success-text">{{ successMsg }}</div>

        <div v-if="!successMsg" class="button-group">
          <button
            class="dialog-btn primary"
            @click="handleConfirm"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            {{ isLoading ? '確認中...' : '確認' }}
          </button>
          <button
            class="dialog-btn secondary"
            @click="handleCancel"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.password-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 330;
}

.password-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.dialog-header h2 {
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #1f2937;
}

.dialog-content {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
  margin-bottom: 4px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  margin-top: 4px;
  font-size: 12px;
}

.success-text {
  color: #10b981;
  margin-bottom: 16px;
  padding: 12px;
  background: #ecfdf5;
  border-radius: 4px;
  font-size: 14px;
}

.button-group {
  display: flex;
  gap: 8px;
}

.dialog-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.dialog-btn.primary {
  background: #3b82f6;
  color: white;
}

.dialog-btn.primary:hover:not(:disabled) {
  background: #2563eb;
}

.dialog-btn.secondary {
  background: #f3f4f6;
  color: #6b7280;
}

.dialog-btn.secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.dialog-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  color: white;
}

.dialog-btn.secondary:disabled {
  background: #d1d5db;
  color: #6b7280;
}
</style>
